import { useState } from 'react'
import { toast } from 'react-toastify'

import { dietBase } from '../api/services/dietbase'
import {
  Folder,
  FolderCategories,
  FolderDiets
} from '../api/services/dietbase/types.ts'

const useDietBaseFolders = () => {
  const [loading, setLoading] = useState(false)
  const [categoryTitles, setCategoryTitles] = useState<string[]>([])
  const [categoryObjects, setCategoryObject] = useState<FolderCategories[]>([])
  const [lastAddedCategory, setLastAddedCategory] =
    useState<FolderCategories | null>(null)
  const [lastAddedFolder, setLastAddedFolder] = useState<Folder | null>(null)

  const fetchCategories = async () => {
    let toastId
    try {
      setLoading(true)
      const data = await dietBase.getFolderCategories()
      const titles: string[] = []
      data.map((group) => {
        titles.push(group.name)
      })
      setCategoryTitles(titles)
      setCategoryObject(data)
    } catch (error) {
      toastId = toast.loading('Pobieranie folderów z dietą...')

      toast.update(toastId, {
        render: 'Wystąpił błąd podczas pobierania folderów z dietą',
        type: 'error',
        delay: 500,
        isLoading: false,
        autoClose: 3000
      })
      console.error('Error:', error)
    } finally {
      toast.dismiss(toastId)
      setLoading(false)
    }
  }

  const createNewCategory = async (categoryName: string) => {
    let toastId
    toastId = toast.loading('Wysyłanie danych...')

    try {
      setLoading(true)
      const data = await dietBase.postFolderCategories({ categoryName })

      if (data !== undefined) {
        const updated = [...(categoryObjects ?? []), data]
        setLastAddedCategory(data)
        setCategoryObject(updated)
      }

      toast.update(toastId, {
        render: 'Nowa kategoria została dodana.',
        type: 'success',
        delay: 500,
        isLoading: false,
        autoClose: 2500
      })
    } catch (error) {
      toast.dismiss(toastId)
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const editCategoryName = async (categoryID: number, categoryName: string) => {
    let toastId
    toastId = toast.loading('Wysyłanie danych...')

    try {
      setLoading(true)
      await dietBase.putFolderCategories({
        categoryID,
        categoryName
      })

      toast.update(toastId, {
        render: 'Nazwa kategorii zmieniona',
        type: 'success',
        delay: 500,
        isLoading: false,
        autoClose: 2500
      })
      return true
    } catch (error) {
      toast.dismiss(toastId)
      console.error('Error:', error)
      return false
    } finally {
      setLoading(false)
    }
  }

  const removeCategory = async (categoryID: number) => {
    let toastId
    toastId = toast.loading('Usuwanie kategorii...')

    try {
      setLoading(true)
      await dietBase.deleteFolderCategory({
        categoryID
      })

      toast.update(toastId, {
        render: 'Kategoria została usunięta',
        type: 'success',
        delay: 500,
        isLoading: false,
        autoClose: 2500
      })
      return true
    } catch {
      toast.dismiss(toastId)
      return false
    } finally {
      setLoading(false)
    }
  }

  const fetchFolders = async (
    folderNameID: number,
    patientProfileID?: number
  ) => {
    const folders: Folder[] = []
    const energyRange: (string | number)[] = []

    const request =
      patientProfileID !== undefined
        ? dietBase.getFolders(folderNameID, patientProfileID)
        : dietBase.getUserFolders(folderNameID)

    try {
      setLoading(true)
      const data = await request
      data.map((folder) => {
        folders.push({
          ...folder,
          selected: false
        })
        energyRange.push(folder.kcal)
      })
      energyRange.push('wszystkie opcje')

      return { folders, energyRange }
    } catch (error) {
      console.error('Error fetching diet base folders:', error)
      return { folders, energyRange }
    } finally {
      setLoading(false)
    }
  }

  const createNewFolder = async (
    categoryID: number,
    kcal: string,
    meals: string
  ) => {
    let toastId
    toastId = toast.loading('Wysyłanie danych...')

    try {
      setLoading(true)
      const data = await dietBase.postFolder({ categoryID, kcal, meals })

      if (data !== undefined) {
        setLastAddedFolder(data)
      }

      toast.update(toastId, {
        render: 'Nowy folder został dodany.',
        type: 'success',
        delay: 500,
        isLoading: false,
        autoClose: 2500
      })
    } catch (error) {
      toast.dismiss(toastId)
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

   const editFolderDetails = async (
     folderID: number,
     categoryID: number,
     kcal: string,
     meals: string,
   ) => {
     let toastId
     toastId = toast.loading('Wysyłanie danych...')

     try {
       setLoading(true)
       await dietBase.putFolder({
         folderID,
         categoryID,
         kcal,
         meals
       })

       toast.update(toastId, {
         render: 'Szczegóły folderu zostały zmienione',
         type: 'success',
         delay: 500,
         isLoading: false,
         autoClose: 2500
       })
       return true
     } catch (error) {
       toast.dismiss(toastId)
       console.error('Error:', error)
       return false
     } finally {
       setLoading(false)
     }
   }

   const removeFolder = async (folderID: number) => {
     let toastId
     toastId = toast.loading('Usuwanie folderu...')

     try {
       setLoading(true)
       await dietBase.deleteFolder({
         folderID
       })

       toast.update(toastId, {
         render: 'Folder został usunięty',
         type: 'success',
         delay: 500,
         isLoading: false,
         autoClose: 2500
       })
       return true
     } catch {
       toast.dismiss(toastId)
       return false
     } finally {
       setLoading(false)
     }
   }

  const fetchDiets = async (folderNameID: number) => {
    const diets: FolderDiets[] = []

    try {
      setLoading(true)
      const data = await dietBase.getDietList(folderNameID)
      return { diets: data.diets }
    } catch (error) {
      console.error('Error fetching diet base folders:', error)
      return { diets }
    } finally {
      setLoading(false)
    }
  }

    const fetchDietDetails = async (dietID: number) => {
      try {
        setLoading(true)
        return await dietBase.getDietDetails(dietID)
      } catch (error) {
        console.error('Error fetching diet details:', error)
        return undefined
      } finally {
        setLoading(false)
      }
    }

       const removeDiet = async (dietId: number) => {
         let toastId
         toastId = toast.loading('Usuwanie diety...')

         try {
           setLoading(true)
           await dietBase.deleteDiet({
             dietId
           })

           toast.update(toastId, {
             render: 'Dieta została usunięta',
             type: 'success',
             delay: 500,
             isLoading: false,
             autoClose: 2500
           })
           return true
         } catch {
           toast.dismiss(toastId)
           return false
         } finally {
           setLoading(false)
         }
       }

  return {
    loading,
    categoryTitles,
    categoryObjects,
    fetchFolders,
    fetchCategories,
    createNewCategory,
    editCategoryName,
    removeCategory,
    lastAddedCategory,
    setLastAddedCategory,
    createNewFolder,
    lastAddedFolder,
    editFolderDetails,
    removeFolder,
    fetchDiets,
    fetchDietDetails,
    removeDiet
  }
}

export default useDietBaseFolders
